import React, { useRef, useState, useEffect, useCallback } from "react";
import BreadCrumb from "Common/BreadCrumb";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { TailSpin } from "react-loader-spinner";
import { X, UploadCloud } from "lucide-react";
import Select from "react-select";
import JoditEditor from "jodit-react";
import AsyncSelect from "react-select/async";
import axios from "axios";
import { useFormik } from "formik";
import * as Yup from "yup";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import _ from "lodash";
import callApi from "Common/utils/apiCaller";

const url = "https://blog-api.supersalesiq.com";

const BlogForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const blogId = queryParams.get('id');
  
  const [loader, setLoader] = useState<any>(false);
  const [imageLink, setImageLink] = useState<any>("");
  const [blogType, setBlogType] = useState<any>("blog");
  const [authorId, setAuthorId] = useState<any>("");
  const [eventData, setEventData] = useState<any>();
  const [isEdit, setIsEdit] = useState<boolean>(!!blogId);
  const [authorList, setAuthorList] = useState<any>();
  const [category, setCategory] = useState([]);
  const [CategoryId, setCategoryId] = useState<any>();
  const editor = useRef(null);
  const [content, setContent] = useState<any>("");
  const [author, setAuthor] = useState<any>();
  
  // Fetch blog data if in edit mode
  useEffect(() => {
    if (blogId) {
      fetchBlogData(blogId);
    }
  }, [blogId]);

  const fetchBlogData = async (id: string) => {
    try {
      const response = await callApi(`v1/blog/${id}`, "get", {});
      if (response?.data?.status === "Success") {
        const blogData = response.data.data.blog;
        setEventData(blogData);
        setImageLink(blogData.thumbnail_url || "");
        setContent(blogData.text || "");
        setBlogType(blogData.blog_type || "blog");
        
        if (blogData.blog_category) {
          setCategoryId({
            label: blogData.blog_category,
            value: blogData.blog_category,
          });
        }
        
        if (blogData.author_id) {
          setAuthorId(blogData.author_id._id);
        }
        
        validation.setValues({
          heading: blogData.heading || "",
          blog_category: blogData.blog_category || "",
        });
      }
    } catch (error) {
      console.error("Error fetching blog data:", error);
      toast("Error fetching blog data");
    }
  };

  // Get authors list
  const onAutherList = async () => {
    const localData = await callApi("v1/blog-author/list", "post", {
      pageNum: 1,
      pageSize: 100,
    });
    let arr = [];
    if (localData?.data?.status === "Success") {
      for (let item of localData?.data?.data.blog_authorList) {
        arr.push({ label: item.name, value: item.name });
      }
      setAuthorList(arr);
      setAuthor(localData?.data?.data.blog_authorList);
    }
  };

  useEffect(() => {
    if (!authorList) {
      onAutherList();
    }
  }, [authorList]);

  useEffect(() => {
    fetchCategory();
  }, []);

  const fetchCategory = async () => {
    const localData = await callApi("v1/blog_category/list", "post", {
      filters: {},
    });
    if (localData?.data.status === "Success") {
      setCategory(
        localData.data.data.blog_categoryList.map((item) => {
          return {
            label: item.name,
            value: item.slug,
          };
        })
      );
    }
  };

  const handleAuthorList = (selectedOption: any) => {
    if (selectedOption.value === "Select...") {
      setAuthorId("");
    } else {
      for (let item of author) {
        if (item.name === selectedOption.value) {
          setAuthorId(item._id);
        }
      }
    }
  };

  const handleRemoveImage = () => {
    setImageLink("");
  };

  const uploadPhoto = async (file: any) => {
    let bodyFormData = new FormData();
    bodyFormData.append("file", file);
    setLoader(true);
    const res = await axios({
      method: "post",
      url: `${url}/api/v1/media/upload`,
      data: bodyFormData,
      headers: { "Content-Type": "multipart/form-data" },
    });

    if (res?.data?.status === "Success") {
      setLoader(false);
      setImageLink(res.data.data.link);
    }
  };

  // Editor configuration
  const config = {
    readonly: false,
    height: 400,
    uploader: {
      insertImageAsBase64URI: true,
    },
    buttons: [
      "bold",
      "italic",
      "underline",
      "|",
      "ul",
      "ol",
      "|",
      "outdent",
      "indent",
      "|",
      "image",
      "|",
      "align",
      "undo",
      "redo",
      "|",
      "hr",
      "eraser",
      "fullsize",
    ],
  };

  // Form validation
  const validation: any = useFormik({
    enableReinitialize: true,
    initialValues: {
      heading: (eventData && eventData.heading) || "",
      blog_category: (eventData && eventData.blog_category) || "",
    },
    validationSchema: Yup.object({
      heading: Yup.string().required("Please Enter Title"),
    }),
    onSubmit: async (values) => {
      if (isEdit) {
        try {
          const res = await callApi(
            `v1/blog/${eventData?._id}/update`,
            "post",
            {
              blog: {
                ...values,
                blog_type: blogType,
                thumbnail_url: imageLink,
                text: content,
                author: authorId,
                blog_category: CategoryId?.value,
              },
            }
          );
          if (res && res?.data?.status === "Success") {
            toast("Updated successfully");
            setImageLink("");
            setContent("");
            setTimeout(() => {
              navigate("/blog");
            }, 1500);
          }
        } catch (e) {
          toast("Something went wrong");
        }
      } else {
        try {
          // Ensure we're sending a properly formatted request that the API expects
          const res = await callApi(`v1/blog/new-blog`, "post", {
            blog: {
              blog_type: blogType || "blog",
              author_id: authorId,
              thumbnail_url: imageLink || "",
              text: content || "",
              heading: values?.heading || "",
              blog_category: CategoryId?.value || "",
              is_active: true, // Set active by default
            },
          });
          
          console.log("Blog creation response:", res?.data);
          if (res && res?.data?.status === "Success") {
            toast("Blog created successfully");
            setImageLink("");
            setContent("");
            setBlogType("");
            setTimeout(() => {
              navigate("/blog");
            }, 1500);
          }
        } catch (e) {
          toast("Something went wrong");
        }
      }
    },
  });

  const options = [
    { value: "blog", label: "blog" },
  ];

  const handleChange = (selectedOption: any) => {
    if (selectedOption.value === "Select...") {
      setBlogType("");
    } else {
      setBlogType(selectedOption.value);
    }
  };

  // Author search functionality
  interface autherOption {
    readonly value: string;
    readonly label: string;
  }
  const opAuther: readonly autherOption[] = authorList;

  const filterAuthor = (inputValue: string = "") => {
    return opAuther.filter((i: any) =>
      i.label?.toLowerCase().includes(inputValue.toLowerCase())
    );
  };

  const loadOptions = (
    inputValue: string,
    callback: (options: autherOption[]) => void
  ) => {
    setTimeout(() => {
      callback(filterAuthor(inputValue));
    }, 1000);
  };

  return (
    <>
      <ToastContainer closeButton={false} limit={1} />
      <BreadCrumb title="Add Blog" pageTitle="Blogs" />
      <div className="grid grid-cols-12 gap-x-5">
        <div className="col-span-12 card 2xl:col-span-12">
          <div className="card-body">
            <div className="flex items-center">
              <h6 className="text-15 grow">{isEdit ? "Edit Blog" : "Add Blog"}</h6>
              <div className="shrink-0">
                <Link
                  to="/blog"
                  className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
                >
                  Back to Blogs
                </Link>
              </div>
            </div>
          </div>
          <div className="card-body">
            <form
              className="create-form"
              id="create-form"
              onSubmit={(e) => {
                e.preventDefault();
                validation.handleSubmit();
                return false;
              }}
            >
              <input type="hidden" value="" name="id" id="id" />
              <input type="hidden" value="add" name="action" id="action" />
              <input type="hidden" id="id-field" />
              <div
                id="alert-error-msg"
                className="hidden px-4 py-3 text-sm text-red-500 border border-transparent rounded-md bg-red-50 dark:bg-red-500/20"
              ></div>
              <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
                <div className="xl:col-span-12">
                  <label
                    htmlFor="blog-type-select"
                    className="inline-block mb-2 text-base font-medium"
                  >
                    Blog Type
                  </label>
                  <Select
                    className="border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                    options={options}
                    isSearchable={true}
                    defaultValue={options[0]}
                    onChange={(event: any) => handleChange(event)}
                    id="blog-type-select"
                    instanceId="blog-type-select"
                    aria-label="Select blog type"
                  />
                </div>
                <div className="xl:col-span-12">
                  <label
                    htmlFor="blog-title"
                    className="inline-block mb-2 text-base font-medium"
                  >
                    Title
                  </label>
                  <input
                    type="text"
                    id="blog-title"
                    className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                    placeholder="Enter Title"
                    name="heading"
                    onChange={validation.handleChange}
                    value={validation.values.heading || ""}
                    aria-required="true"
                  />
                  {validation.touched.heading && validation.errors.heading ? (
                    <p id="blog-title-error" className="text-red-400" aria-live="polite">{validation.errors.heading}</p>
                  ) : null}
                </div>
                <div className="xl:col-span-12">
                  <label
                    htmlFor="blog-author-select"
                    className="inline-block mb-2 text-base font-medium"
                  >
                    Author
                  </label>
                  <div className="xl:col-span-2">
                    {authorList ? (
                      <AsyncSelect
                        className="border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                        cacheOptions
                        loadOptions={loadOptions}
                        onChange={(event: any) => handleAuthorList(event)}
                        id="blog-author-select"
                        instanceId="blog-author-select"
                        aria-label="Select blog author"
                        defaultOptions
                      />
                    ) : null}
                  </div>
                </div>
                <div className="w-64 md:w-[700px] xl:col-span-2">
                  <label htmlFor="blog-category-select" className="inline-block mb-2 text-base font-medium">
                    Category
                  </label>
                  <Select
                    className="border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                    options={category}
                    isSearchable={false}
                    onChange={(event: any) => setCategoryId(event)}
                    id="blog-category-select"
                    instanceId="blog-category-select"
                    aria-label="Select blog category"
                    value={CategoryId}
                  />
                </div>
                <div className="xl:col-span-12">
                  <label
                    htmlFor="blog-content-editor"
                    className="inline-block mb-2 text-base font-medium"
                  >
                    Text
                  </label>
                  <div id="blog-content-editor" aria-label="Blog content editor">
                    <JoditEditor
                      ref={editor}
                      config={config}
                      value={content}
                      onBlur={(newContent) => setContent(newContent)}
                      onChange={(newContent) => {}}
                    />
                  </div>
                </div>
              </div>
              {imageLink === "" ? (
                loader ? (
                  <div
                    className="flex justify-center"
                    style={{ marginTop: "10px" }}
                  >
                    <TailSpin
                      visible={true}
                      height="50"
                      width="50"
                      color="#4fa94d"
                    />
                  </div>
                ) : (
                  <div
                    style={{ border: "1px solid #e2e8f0", padding: "10px" }}
                    className="flex items-center justify-center gap-2 mt-4 border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 "
                  >
                    <input
                      type="file"
                      id="blog-image-upload"
                      accept="image/*"
                      className="hidden profile-img-file-input"
                      onChange={(e: any) => uploadPhoto(e.target.files[0])}
                      aria-label="Upload blog image"
                    />
                    <label
                      htmlFor="blog-image-upload"
                      className="flex flex-col items-center justify-center cursor-pointer "
                    >
                      <div className="mb-3">
                        <UploadCloud className="block mx-auto size-12 text-slate-500 fill-slate-200 dark:text-zink-200 dark:fill-zink-500"></UploadCloud>
                      </div>

                      <h5 className="mb-0 font-normal text-slate-500 text-15">
                        Drag and drop your files or <a href="#!">browse</a> your
                        files
                      </h5>
                    </label>
                  </div>
                )
              ) : (
                <div>
                  <div
                    className="flex flex-wrap justify-center gap-2 mt-4"
                    style={{
                      border: "1px solid #e2e8f0",
                      position: "relative",
                    }}
                  >
                    <>
                      <img
                        src={imageLink}
                        width={100}
                        alt={"img"}
                        style={{
                          margin: "10px",
                          borderRadius: "10px",
                          height: "100px",
                        }}
                      />

                      <div
                        style={{}}
                        onClick={handleRemoveImage}
                      >
                        <X></X>
                      </div>
                    </>
                  </div>
                </div>
              )}
              <div className="flex justify-end gap-2 mt-4">
                <Link
                  to="/blog"
                  className="text-red-500 bg-white btn hover:text-red-500 hover:bg-red-100 focus:text-red-500 focus:bg-red-100 active:text-red-500 active:bg-red-100 dark:bg-zink-600 dark:hover:bg-red-500/10 dark:focus:bg-red-500/10 dark:active:bg-red-500/10"
                >
                  Cancel
                </Link>
                <button
                  type="submit"
                  id="addNew"
                  className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
                >
                  {!!isEdit ? "Update Blog" : "Add Blog"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default BlogForm;
